import pdfMake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import useDate from '@/services/utils/day';
import figureFormatter from "@/services/utils/figureFormatter";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

const robotoFonts = {
    Roboto: {
        normal: 'Roboto-Regular.ttf',
        bold: 'Roboto-Medium.ttf',
        italics: 'Roboto-Italic.ttf',
        bolditalics: 'Roboto-MediumItalic.ttf'
    }
};

const { formatDate } = useDate();
const { commaFormat } = figureFormatter();

const pdfPrinter = () => {
    
    const exportToPDF = (company, stockLedgerHeaderData, stockLedgerData, userName) => {
        var doc = {
            pageSize: 'A4',
            pageMargins: [ 30, 100, 30, 80 ],
            pageOrientation: 'landscape',
            header: getHeader(company),
            footer: getFooter(userName),
            content: getContent(stockLedgerHeaderData, stockLedgerData),
            styles : {
                header: {
                    fontSize: 24
                }
            },
            defaultStyle: {
                color: 'black',
                fontSize: 9,
            },
            info: {
                title: 'Stock Ledger'
            }
        }

        const pdfDocGenerator = pdfMake.createPdf(doc, null, robotoFonts);
        pdfDocGenerator.open();
    }

    const getContent = (stockLedgerHeaderData, stockLedgerData) => {
        return [
            {
                text: 'Stock Ledger',
                style: 'header',
                alignment: 'center',
                margin: [0, 0, 20, 10],
                bold: true,
                color: 'black',
                decoration: 'underline'
            },
            getHeaderSection(stockLedgerHeaderData),
            { text: '', margin: [0, 5] },
            getStockLedgerTable(stockLedgerHeaderData, stockLedgerData)
        ]
    }

    const getStockLedgerTable = (headerData, balances) => {
        const openingQty = headerData.opening_stock || 0;
        const closingQty = headerData.closing_stock || 0;
        const openingValue = headerData.opening_value || 0;
        const closingValue = headerData.closing_value || 0;

        const totalInQty = headerData.in_quantity || 0;
        const totalOutQty = headerData.out_quantity || 0;
        const totalInAmount = headerData.in_amount || 0;
        const totalOutAmount = headerData.out_amount || 0;
        const saleValue = headerData.sale_value || 0;
        const profitValue = headerData.profit_value || 0;

        const body = [];

        // ---- Table Header ----
        const headerRow = [
            { text: 'Date', bold: true },
            { text: 'Txn Type', bold: true },
            { text: 'Txn Number', bold: true },
            { text: 'Party Name', bold: true },
            { text: 'Tracking Ref', bold: true },
            { text: 'Rate', alignment: 'right', bold: true },
            { text: 'In (Qty)', alignment: 'right', bold: true },
            { text: 'Out (Qty)', alignment: 'right', bold: true },
            { text: 'Balance (Qty)', alignment: 'right', bold: true },
            { text: 'In (Value)', alignment: 'right', bold: true },
            { text: 'Out (Value)', alignment: 'right', bold: true },
            { text: 'Balance (Value)', alignment: 'right', bold: true },
            { text: 'Sale (Value)', alignment: 'right', bold: true },
            { text: 'Profit (Value)', alignment: 'right', bold: true },
        ];
        body.push(headerRow);

        // ---- Opening Row ----
        body.push([
            { text: 'Opening Balance', colSpan: 6, alignment: 'right', bold: true }, {}, {}, {}, {}, {},
            {}, {},
            { text: commaFormat(openingQty), alignment: 'right' },
            {}, {},
            { text: commaFormat(openingValue), alignment: 'right' },
            {}, {}
        ]);

      // ---- Data Rows ----
        if (balances.length) {
            balances.forEach((item) => {
                body.push([
                    item.date || '',
                    item.txn_type || '',
                    item.txn_number || '',
                    item.party_name || '',
                    item.stock_ref || '',
                    { text: commaFormat(item.rate || 0), alignment: 'right' },
                    { text: commaFormat(item.in_quantity || 0), alignment: 'right' },
                    { text: commaFormat(item.out_quantity || 0), alignment: 'right' },
                    { text: commaFormat(item.balance_stock || 0), alignment: 'right' },
                    { text: commaFormat(item.in_amount || 0), alignment: 'right' },
                    { text: commaFormat(item.out_amount || 0), alignment: 'right' },
                    { text: commaFormat(item.balance_value || 0), alignment: 'right' },
                    { text: commaFormat(item.sale_value || 0), alignment: 'right' },
                    { text: commaFormat(item.profit_value || 0), alignment: 'right' },
                ]);
            });
        }

        body.push([
            { text: 'Closing Balance', colSpan: 6, alignment: 'right', bold: true }, {}, {}, {}, {}, {},
            { text: commaFormat(totalInQty), alignment: 'right', bold: true },
            { text: commaFormat(totalOutQty), alignment: 'right', bold: true },
            { text: commaFormat(closingQty), alignment: 'right', bold: true },
            { text: commaFormat(totalInAmount), alignment: 'right', bold: true },
            { text: commaFormat(totalOutAmount), alignment: 'right', bold: true },
            { text: commaFormat(closingValue), alignment: 'right', bold: true },
            { text: commaFormat(saleValue), alignment: 'right', bold: true },
            { text: commaFormat(profitValue), alignment: 'right', bold: true },
        ]);

        return {
            style: 'tableExample',
            table: {
                headerRows: 1,
                widths: [
                    50, 60, 50, '*', 50,
                    30, 45, 45, 45, 40, 40, 50, 50, 50
                ],
                body,
            },
            layout: {
                fillColor: (rowIndex) => (rowIndex === 0 ? '#f0f0f0' : null),
                hLineWidth: () => 0.3,
                vLineWidth: () => 0.3,
            },
        };
    };

    const getHeaderSection = (stockLedgerHeaderData) => {
      return {
        margin: [0, 0, 0, 10],
        columns: [
          {
            width: '*',
            stack: [
                { 
                  text: [
                    { text: 'Product/Service Name: ' },
                    { text: stockLedgerHeaderData.product_name || '', bold: true }
                  ],
                  noWrap: true,
                  margin: [0, 0, 0, 2]
                },
                { 
                  text: [
                    { text: 'Report Period: ' },
                    { text: stockLedgerHeaderData.report_period || '', bold: true }
                  ],
                  margin: [0, 0, 0, 2]
                },
            ]
          },

          {
            width: 'auto',
            table: {
              widths: [70, 50],
              body: [
                [
                  { text: 'Opening (Qty):', alignment: 'left' },
                  { text: commaFormat(stockLedgerHeaderData.opening_stock || 0), alignment: 'right', bold: true }
                ],
                [
                  { text: 'In (Qty):', alignment: 'left' },
                  { text: commaFormat(stockLedgerHeaderData.in_quantity || 0), alignment: 'right', bold: true }
                ],
                [
                  { text: 'Out (Qty):', alignment: 'left' },
                  { text: commaFormat(stockLedgerHeaderData.out_quantity || 0), alignment: 'right', bold: true }
                ],
                [
                  { text: 'Closing (Qty):', alignment: 'left' },
                  { text: commaFormat(stockLedgerHeaderData.closing_stock || 0), alignment: 'right', bold: true }
                ]
              ]
            },
            layout: 'noBorders',
            margin: [0, 0, 20, 0]
          },

          {
            width: 'auto',
            table: {
              widths: [70, 50],
              body: [
                [
                  { text: 'Opening (Value):', alignment: 'left' },
                  { text: commaFormat(stockLedgerHeaderData.opening_value || 0), alignment: 'right', bold: true }
                ],
                [
                  { text: 'In (Value):', alignment: 'left' },
                  { text: commaFormat(stockLedgerHeaderData.in_amount || 0), alignment: 'right', bold: true }
                ],
                [
                  { text: 'Out (Value):', alignment: 'left' },
                  { text: commaFormat(stockLedgerHeaderData.out_amount || 0), alignment: 'right', bold: true }
                ],
                [
                  { text: 'Closing (Value):', alignment: 'left' },
                  { text: commaFormat(stockLedgerHeaderData.closing_value || 0), alignment: 'right', bold: true }
                ]
              ]
            },
            layout: 'noBorders'
          }
        ]
      };
    };

    const getHeader = (company) => {
        return {
            margin: [ 30, 25, 30, 10 ],
            columns: [
                {
                    alignment: 'left',
                    image: company.logo64,
                    maxHeight: 60
                },
                {
                    alignment: 'right',
                    stack: [
                        company.name,
                        'Address: ' + company.address,
                        'Phone: ' + company.phone,
                        'E-mail: ' + company.email
                    ]
                }
            ]
        }
    }

    const getFooter = (userName) =>{
        return {
            margin: [ 30, 0 ],
            columns: [
                {
                    columns: [
                        {
                            width: '90%',
                            alignment: 'left',
                            text: 'N.B: This is a system generated documents and requires no manual signature.',
                            margin: [5, 53, 0, 0],
                            fontSize: 8
                        }
                    ]
                },
                {
                    width: '20%',
                    alignment: 'right',
                    fontSize: 8,
                    margin: [0, 33, 0, 0],
                    stack: [
                        {
                            text: 'Printed by',
                            bold: true
                        },
                        {
                            text: userName
                        },
                        {
                            text: formatDate(new Date())
                        }
                    ]
                }
            ]
        }
    }

    return {
        exportToPDF
    }
}

export default pdfPrinter;
